import {RefObject, useEffect, useRef} from 'react'

/**
 * Adds keyboard navigation to the dropdown options
 */
export const useDropdownOptionAccessability = <
  R extends HTMLElement = HTMLElement
>({
  optionSelector, // the selector for the options, i.e. .option
  focusFirstOnOpen = true, // whether to focus the first option when the dropdown opens
  reAttachListenersId // listeners will be reattached when this changes
}: {
  optionSelector: string
  focusFirstOnOpen?: boolean
  reAttachListenersId?: string
}): [
  RefObject<R> // attach this to the options container
] => {
  const optionsRef = useRef<R>(null)
  const hasFocusedRef = useRef(false)

  // When displaying the dropdown, focus the first option and add keyboard navigation
  useEffect(() => {
    if (!optionsRef.current) {
      return
    }

    const options = Array.from(
      // Find all options under optionsRef
      optionsRef.current.querySelectorAll(optionSelector)
    ) as HTMLElement[]

    // Ensure options focusable
    options.forEach(option => {
      option.setAttribute('tabindex', '0')
    })

    if (focusFirstOnOpen && !hasFocusedRef.current) {
      //find currently selected else default to first
      const focusOption = (options.find(o =>
        o.classList.contains('selected')
      ) ?? options?.[0]) as HTMLElement | undefined
      if (focusOption) {
        focusOption.focus()
        hasFocusedRef.current = true
      }
    }

    const keyListener = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null
      if (!target) {
        return
      }
      if (event.key === 'Enter') {
        event.preventDefault()
        // Trigger a click, use the first input we can find, otherwise first child.
        const clickTarget = (target.querySelector('input') ??
          target.firstChild) as HTMLElement

        if (clickTarget) {
          const clickEvent = new MouseEvent('click', {
            bubbles: true, // This ensures the event goes through the bubbling phase
            cancelable: true,
            view: window
          })
          clickTarget.dispatchEvent(clickEvent)
        }
      } else if (event.key === 'ArrowDown') {
        const currentIndex = options.indexOf(target)
        const nextIndex = (currentIndex + 1) % options.length
        const nextOption = options[nextIndex]
        if (nextOption) {
          nextOption.focus()
        }
      } else if (event.key === 'ArrowUp') {
        const currentIndex = options.indexOf(target)
        const nextIndex = (currentIndex - 1 + options.length) % options.length
        const previousOption = options[nextIndex]
        if (previousOption) {
          previousOption.focus()
        }
      }
    }

    options.forEach(option => {
      option.addEventListener('keydown', keyListener)
    })

    return () => {
      options.forEach(option => {
        option.removeEventListener('keydown', keyListener)
      })
    }
  }, [optionSelector, focusFirstOnOpen, reAttachListenersId])

  return [optionsRef] as const
}
